import React from 'react';
import PageTitle from './elements/PageTitle';
import PageController from './Page.controller';
import Inherit from '../../../core/inherit';
import Layout from '../layouts/Layout';
import Loading from '../Loading';
import NoPermissionFallback from '../roleGuard/elements/NoPermissionFallback';
import { Tabs } from '@clockout-dev/design-elements';
import { Result } from 'antd';
import GoBackButton from './elements/GoBackButton';

/**
 * Page component with integrated scope-based access control.
 * @param {Object} title - The title object
 * @param {string} title.text - The text of the title
 * @param {string} title.highlight - The highlight of the title
 * @param {string} description - The description of the page
 * @param {JSX.Element} [action] - Optional action rendered on the right side of the title row
 * @param {Object[]} [tabs] - Optional tab definitions. When provided, the page
 *   renders a tab bar and shows the active tab's `content` (when defined),
 *   falling back to `children` if the active tab has no content.
 * @param {string} tabs[].key - Unique tab key (stored in the URL)
 * @param {string} tabs[].label - Tab label
 * @param {JSX.Element} [tabs[].content] - Content rendered when the tab is active
 * @param {boolean} [tabs[].disabled] - Disables the tab in the strip
 * @param {JSX.Element} [tabs[].icon] - Optional leading icon for the tab
 * @param {string} [tabsSearchParamKey='tab'] - URL search param used by the tabs
 * @param {string} [activeTabKey] - Optional controlled active tab key
 * @param {(key: string) => void} [onTabChange] - Optional callback fired
 *   when the active tab changes. The new tab key is also persisted to the
 *   URL search param.
 * @param {boolean} [isLoading] - When true, renders the loading state
 * @param {string | null} [scope] - Scope key required to view this page (e.g.
 *   'screen:dashboard:view'). Pass `null`/omit to skip validation. Checked
 *   via `ClockoutController.hasScope()` against the session user's resolved
 *   scopes — role *names* are never compared, since they're per-FI and
 *   editable from the Role Composer.
 * @param {JSX.Element} [fallback] - Content rendered when access is denied. Defaults to NoPermissionFallback.
 * @returns {JSX.Element} The Page component
 */
class Page extends Inherit(PageController, React.Component) {
    render() {
        const {
            title,
            description,
            action,
            tabs,
            children,
            isLoading,
            error,
            goBack,
            scope,
            fallback,
        } = this.props;
        const hasTabs = Array.isArray(tabs) && tabs.length > 0;
        const activeTabKey = this.resolveActiveTabKey();
        const notificationContextHolder = this.props.notificationContextHolder;

        if (error && error?.isError) {
            return (
                <Layout user={this.user} fi={this.user?.fi?.name}>
                    <Result
                        status="error"
                        title={error?.title}
                        subTitle={error?.subTitle}
                        extra={error?.content}
                    />
                </Layout>
            );
        }

        // Session hasn't hydrated yet — defer the scope check rather than
        // momentarily rendering the deny state before `this.user` is set.
        if (scope && !this.user) {
            return (
                <Layout user={this.user} fi={this.user?.fi?.name}>
                    <Loading />
                </Layout>
            );
        }

        if (scope && !this.hasScope(scope)) {
            return (
                <Layout user={this.user} fi={this.user?.fi?.name}>
                    {fallback !== undefined ? fallback : <NoPermissionFallback />}
                </Layout>
            );
        }

        return (
            <Layout user={this.user} fi={this.user?.fi?.name}>
                {notificationContextHolder}
                {goBack ? <GoBackButton label={goBack.label} onClick={goBack.onClick} /> : null}
                {title || description || action ? (
                    <PageTitle title={title} description={description} action={action} />
                ) : null}
                {hasTabs ? (
                    <Tabs
                        items={tabs.map(({ key, label, icon, disabled }) => ({
                            key,
                            label,
                            icon,
                            disabled,
                        }))}
                        activeKey={activeTabKey}
                        onChange={this.handleTabChange.bind(this)}
                    />
                ) : null}
                {isLoading ? <Loading /> : children}
            </Layout>
        );
    }
}

export default Page;
